import * as React from 'react'
import { useEffect } from 'react'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Accordion } from '@/components/ui/accordion'
import AppNavbarLink from './app-navbar-link'
import NavbarLogo from './navbar-logo'
import NavbarCartButton from './navbar-cart-button'
import MobileNavSection from './mobile-nav-section'
import {
  adminItems,
  adventuresItems,
  advisorItems,
  storeItems,
  superadminItems,
  userMenuItems,
} from '../../config/navbar-data'
import { useUserStore } from '../../stores/user-store'
import {
  filterNavbarSections,
  filterUserMenuItems,
  shouldShowAdminMenu,
  shouldShowAdvisorMenu,
  shouldShowMemberCTA,
} from '../../lib/navigation-utils'

export default function MobileNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const { user, tenant, fetchUserData, fetchCartCount } = useUserStore()

  useEffect(() => {
    fetchUserData()
    fetchCartCount()
  }, [])

  const handleItemClick = () => setMobileMenuOpen(false)

  const filteredAdventures = filterNavbarSections(adventuresItems, user, tenant)
  const filteredStore = filterNavbarSections(storeItems, user, tenant)
  const filteredAdvisor = filterNavbarSections(advisorItems, user, tenant)
  const filteredAdmin = filterNavbarSections(adminItems, user, tenant)
  const filteredSuperadmin = filterNavbarSections(superadminItems, user, tenant)
  const filteredUserMenu = filterUserMenuItems(userMenuItems, user, tenant)

  const showAdvisor = shouldShowAdvisorMenu(user)
  const showAdmin = shouldShowAdminMenu(user)
  const showSuperadmin = user?.superadmin === true
  const showMemberCTA = shouldShowMemberCTA(user, tenant)
  const showHuntPlanCTA = tenant?.hunts_enabled && !tenant?.is_store_only && user?.member

  return (
    <>
      <div className="w-full">
        <div className="px-6">
          <div className="flex items-center justify-between" style={{ height: '117px' }}>
            <NavbarLogo height={70} />

            <div className="flex items-center space-x-4">
              <NavbarCartButton />

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="!h-5 !w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-80 overflow-y-auto px-4">
          <SheetHeader>
            <SheetTitle>
              <NavbarLogo height={65} />
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            {user
              ? (
                  <>
                    <AppNavbarLink
                      href="/dashboard"
                      variant="mobile"
                      onClick={handleItemClick}
                      className="hover:bg-accent/80 rounded-lg px-2"
                    >
                      Dashboard
                    </AppNavbarLink>

                    <Accordion type="single" collapsible className="w-full">
                      {filteredAdventures.length > 0 && (
                        <MobileNavSection
                          title="Adventures"
                          value="adventures"
                          sections={filteredAdventures}
                          onItemClick={handleItemClick}
                        />
                      )}

                      {filteredStore.length > 0 && (
                        <MobileNavSection
                          title="Store"
                          value="store"
                          sections={filteredStore}
                          onItemClick={handleItemClick}
                        />
                      )}

                      {showAdvisor && (
                        <MobileNavSection
                          title="Advisor"
                          value="advisor"
                          sections={filteredAdvisor}
                          onItemClick={handleItemClick}
                        />
                      )}

                      {showAdmin && (
                        <MobileNavSection
                          title="Admin"
                          value="admin"
                          sections={[
                            ...filteredAdmin,
                            ...(showSuperadmin ? filteredSuperadmin : []),
                          ]}
                          onItemClick={handleItemClick}
                        />
                      )}
                    </Accordion>

                    {showMemberCTA && (
                      <div className="pt-4">
                        <Button asChild variant="outline" className="h-11 w-full py-3.5">
                          <a href="/membership_pricing" className="font-sans text-sm normal-case">Become a member</a>
                        </Button>
                      </div>
                    )}

                    {showHuntPlanCTA && user.member && (
                      <div className="pt-4">
                        <Button asChild variant="default" className="h-11 w-full py-3.5">
                          <a href="/hunt_plans/new" className="font-sans text-sm !text-white normal-case">Plan Your Hunt</a>
                        </Button>
                      </div>
                    )}

                    <div className="space-y-2 border-t pt-4">
                      {filteredUserMenu.map((item, index) => (
                        <React.Fragment key={`${item.href}-${index}`}>
                          {item.separator === 'before' && <div className="my-2 border-t" />}
                          <AppNavbarLink
                            href={item.href}
                            data-method={item.dataMethod}
                            variant="mobile"
                            className={item.className}
                            onClick={handleItemClick}
                          >
                            {item.label}
                          </AppNavbarLink>
                          {item.separator === 'after' && <div className="my-2 border-t" />}
                        </React.Fragment>
                      ))}

                      <div className="text-muted-foreground pt-4 pb-2 text-sm">
                        <div>{user.name}</div>
                        {user.active_role && (
                          <div className="text-muted-foreground/70 mt-0.5 text-xs">
                            {user.active_role}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )
              : (
                  <>
                    <AppNavbarLink
                      href="/login"
                      variant="mobile"
                      onClick={handleItemClick}
                    >
                      Login
                    </AppNavbarLink>

                    {tenant?.terms_of_service_url && (
                      <AppNavbarLink
                        href={tenant.terms_of_service_url}
                        variant="mobile"
                        onClick={handleItemClick}
                      >
                        Terms of Service
                      </AppNavbarLink>
                    )}
                  </>
                )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
